import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { ViewportScroller } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { FlatpickrDefaultsInterface, FlatpickrModule } from 'angularx-flatpickr'
import { debounceTime, Subject, Subscription } from 'rxjs'
import { unsubscribeAll } from 'src/app/_helpers/unsubscribe.helper'
import { ArticleSummary, ArticleTemplate, EditEventType, Operation, OperationMetadata, OperationTechnicianEntry, Status, Technician, Ticket, TicketLocation, TicketMetadata } from 'src/app/_models'
import { ArticleService, OperationService, SessionService, TicketService, UserService, ModalService } from 'src/app/_services'
import { EditInputComponent } from "../_shared/edit-input/edit-input.component";
import { StoreInfoEditorComponent } from "./store-info-editor/store-info-editor.component";
import { StoreSubboxComponent } from "./store-subbox/store-subbox.component";
import { NgxMapboxGLModule } from "ngx-mapbox-gl";
import { OperationSubpageComponent } from "./operation-subpage/operation-subpage.component";
import { LazyLoadImageModule } from "ng-lazyload-image";
import { StoreFilterPipe } from "src/app/_pipes";
import { NoteSubpageComponent } from "./note-subpage/note-subpage.component";
import { encodePlanTicketsLoadingCaseParams } from "../planning-assistant/planning-assistant.component";
import { TicketGroup } from "../../_models/ticket-group.interface";
import { TicketGroupService } from "../../_services/ticket-group.service";
import { TooltipModule } from "@cloudfactorydk/ng2-tooltip-directive";
import { TicketDeleteDialogComponent, TicketForDelete } from './ticket-delete-dialog/ticket-delete-dialog.component';

@Component({
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterModule, FlatpickrModule, FontAwesomeModule, EditInputComponent, StoreSubboxComponent, NgxMapboxGLModule, OperationSubpageComponent, LazyLoadImageModule, StoreFilterPipe, NoteSubpageComponent, TooltipModule],
  selector: 'hawk-ticket-page',
  templateUrl: './ticket-page.component.html',
  styleUrls: ['./ticket-page.component.scss'],
  host: {
    class: 'app-page'
  }
})
export class TicketPageComponent implements OnInit, OnDestroy {

  defaultImage = 'https://via.placeholder.com/1500'

  public ticket: Ticket
  public ticketId: string
  public planningAssistantLink = ''
  public operations: Operation[] = []
  public technicians: Technician[] = []
  public dispatchers: Technician[] = []
  public articleTemplates: ArticleTemplate[] = []
  public articleSummary: ArticleSummary
  public selectedOperation: Operation
  public position: [number, number]
  public metaData: TicketMetadata

  private subscriptions: Subscription[] = []

  public isBookkeeper = false

  public technicianTicketLocations: { [key: number]: TicketLocation[] } = {}

  public storeSearchboxValue = ''

  public markerData: { lat: number, lng: number, color: string, text: string }[]

  public paramOpId: string
  public paramQmJobId: string

  public showModal = ''

  public editExtNr = false
  public editDateSla = false
  public editProject = false
  public editStore = false
  public editDescription = false
  public editAddrCompany = false
  public editAddrName = false
  public editAddrStreet = false
  public editAddrZipCity = false
  public editAddrCountry = false
  public editStoreContact = false
  public editDispatcher = false
  public editFlatrateWorktime = false
  public editFlatrateBilling = false
  public editFlatrateMinutes = false

  get hasCustomerContact(): boolean {
    return !!this.ticket?.customer_contact_name
  }


  public selectedStep: OperationMetadata
  public resetStoreUnit: Subject<void> = new Subject<void>();

  public datePickerConfig: FlatpickrDefaultsInterface = {
    enableTime: true,
    // utc: true,
    convertModelValue: true,
    altInput: true,
    allowInput: true,
    altFormat: 'd.m.Y H:i',
    dateFormat: 'Y-m-d H:i',
    defaultHour: 10,
    defaultMinute: 0,
    //
    time24hr: true,
    enable: [{ from: new Date(1985, 1), to: new Date(new Date().getFullYear() + 50, 12) }]
  }
  public selectedDate

  public ticketContactForm = new UntypedFormGroup({
    contact_name: new UntypedFormControl(''),
    contact_email: new UntypedFormControl(''),
    contact_phone: new UntypedFormControl(''),
  })

  public customerContactForm = new UntypedFormGroup({
    customer_contact_salutation: new UntypedFormControl(''),
    customer_contact_name: new UntypedFormControl(''),
    customer_contact_email: new UntypedFormControl(''),
  })

  public prioForm = new UntypedFormGroup({
    priority_id: new UntypedFormControl(''),
  })

  public statusForm = new UntypedFormGroup({
    status_id: new UntypedFormControl(''),
  })

  public metaTimeForm = new UntypedFormGroup({
    time_estimation: new UntypedFormControl(''),
  })

  public appointmentForm = new UntypedFormGroup({
    appointment_date: new UntypedFormControl(''),
    appointment_contact: new UntypedFormControl(''),
  })

  public ticketGroup: TicketGroup
  private ticketGroupHawkIdsInputChanged$ = new Subject<void>();
  public addHawkIdsToTicketGroup: string;  //FSP-23-115087;FSP-23-115089;...
  public addTicketsToTicketGroup: { ticket: Ticket; add_to_group: boolean; info: string; unite_with_group?: TicketGroup }[] = [];
  public saveTicketGroupProcess = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ticketService: TicketService,
    private operationService: OperationService,
    private userService: UserService,
    private sessionService: SessionService,
    private articleService: ArticleService,
    private ticketGroupService: TicketGroupService,
    private viewportScroller: ViewportScroller,
    private modal: ModalService
  ) {
    this.ticketGroupHawkIdsInputChanged$
      .pipe(debounceTime(500)) // erst feuern, wenn 500 ms kein Input
      .subscribe(() => this.validateTicketGroupHawkIds());
  }

  get selectedDispatcher(): string {
    return this.dispatchers.find(d => d.id == this.ticket.dispatcher_id)?.name
  }

  ngOnInit(): void {
    this.ticketId = this.route.snapshot.paramMap.get('id')
    this.planningAssistantLink = encodePlanTicketsLoadingCaseParams(this.router, this.ticketId)
    this.markerData = []

    this.paramOpId = this.route.snapshot.queryParamMap.get('operation_id')
    this.paramQmJobId = this.route.snapshot.queryParamMap.get('qmJob')

    this.loadTicket()
    this.loadPermissions()

    this.subscriptions.push(this.articleService.loadArticleTemplates().subscribe(
      (data) => {
        this.articleTemplates = data
      },
      (err) => {
        console.error(err)
      },
    )
    )

    this.subscriptions.push(this.userService.getDispatcher().subscribe(
      (data) => {
        this.dispatchers = data
      },
      (err) => {
        console.error(err)
      },
    )
    )
  }

  public ngOnDestroy() {
    unsubscribeAll(this.subscriptions)
  }

  public handleReloadTicket(reload: boolean): void {
    if (reload) {
      this.loadTicket();
    }
  }

  public loadTicket() {
    const id = this.ticketId
    this.subscriptions.push(
      this.ticketService.getTicket(id).subscribe(
        (data) => {
          this.updateTicket(data)

          this.subscriptions.push(
            this.ticketService.getOperations(String(this.ticket.id)).subscribe(
              (dataOp) => {
                this.operations = dataOp

                const l = this.operations.length
                if (l > 0) {
                  if (this.paramOpId) {
                    const match = this.operations.filter((op) => op.id.toString() === this.paramOpId)

                    if (match) {
                      this.selectOperation(match[0])
                    }
                  }
                  if (!this.selectedOperation) {
                    this.selectOperation(this.operations[l - 1])
                  }
                  this.loadTicketLocations()
                }

                this.loadArticleSummary()
              },
              (err) => {
                console.error(err)
              },
            )
          )

          this.subscriptions.push(
            this.userService.getProjectTechnicians(data.project_id).subscribe(
              (technicians) => {
                this.technicians = technicians
              },
              (err) => {
                console.error(err)
              },
            )
          )

          this.loadTicketGroup()
        },
        (err) => {
          console.error(err)
        },
      )
    )
  }

  public updateTicket(ticketData: Ticket) {
    if (!ticketData.store_id) this.normalizeAddressStreetFormat(ticketData)
    this.ticket = ticketData
    this.markerData = []

    this.ticketContactForm.controls.contact_name.setValue(this.ticket.contact_name)
    this.ticketContactForm.controls.contact_email.setValue(this.ticket.contact_email)
    this.ticketContactForm.controls.contact_phone.setValue(this.ticket.contact_phone)

    this.customerContactForm.controls.customer_contact_salutation.setValue(this.ticket.customer_contact_salutation || '')
    this.customerContactForm.controls.customer_contact_name.setValue(this.ticket.customer_contact_name || '')
    this.customerContactForm.controls.customer_contact_email.setValue(this.ticket.customer_contact_email || '')

    this.metaTimeForm.controls.time_estimation.setValue(this.ticket.time_estimation)
    this.appointmentForm.controls.appointment_contact.setValue(this.ticket.appointment_contact)
    this.appointmentForm.controls.appointment_date.setValue(this.ticket.appointment_date)

    this.position = [this.ticket.address_longitude, this.ticket.address_latitude]

    this.markerData.push({
      lat: this.ticket.address_latitude,
      lng: this.ticket.address_longitude,
      color: '#af0000',
      text: 'Store'
    })

    this.subscriptions.push(
      this.ticketService.getMetaData(ticketData.id).subscribe(
        (metaData) => {
          this.metaData = metaData

          this.setMeta()
        },
        (err) => {
          console.error(err)
        }
      )
    )
  }

  public createEmptyOperation() {
    this.ticketService.createEmptyOperation(this.ticket.id).subscribe(
      (_) => {
        window.location.reload();
      },
      (err) => {
        console.error(err)
      }
    )
  }

  public loadTicketLocations() {
    this.technicianTicketLocations = {}

    this.operations.forEach((op) => {
      this.subscriptions.push(
        this.ticketService.getTechnicianTicketLocations(op.technicians[0].id, (new Date(op.operation_date)).toISOString().slice(0, 10)).subscribe(
          (locations) => {
            this.technicianTicketLocations[op.technicians[0].id] = locations
            this.selectOperation(this.selectedOperation)
          },
          (err) => {
            console.error(err)
          }
        )
      )
    })
  }

  public loadArticleSummary() {
    this.subscriptions.push(
      this.ticketService.getArticleSummary(this.ticket.id).subscribe(
        (data) => {
          this.articleSummary = data
        },
        (err) => {
          console.error(err)
        }
      )
    )
  }

  public loadPermissions() {
    this.subscriptions.push(
      this.sessionService.rolePermissions().subscribe(
        (data) => {
          if (this.checkPermission(["ACCOUNTANT"], data.roles)) {
            this.isBookkeeper = true
          }

        },
        (err) => {
          console.error(err)
        },
      )
    )
  }

  private checkPermission(validPermissions: string[], userPermissions: string[]): boolean {
    for (const role of userPermissions) {
      if (validPermissions.includes(role)) {
        return true
      }
    }
    return false
  }

  public operationActive(operation: Operation): boolean {
    if (this.selectedOperation) {
      return operation === this.selectedOperation
    } else {
      return false
    }
  }

  public selectOperation(operation: Operation) {
    this.selectedOperation = operation

    this.markerData = []

    this.markerData.push({
      lat: this.ticket.address_latitude,
      lng: this.ticket.address_longitude,
      color: '#af0000',
      text: 'Store'
    })

    if (operation.technicians) {
      const tech = operation.technicians[0]

      if (this.technicianTicketLocations[tech.id]) {
        this.technicianTicketLocations[tech.id].forEach((element) => {
          if (element.latitude !== this.ticket.address_latitude || this.ticket.address_longitude !== element.longitude) {
            this.markerData.push({
              lat: element.latitude,
              lng: element.longitude,
              color: '#af00af',
              text: element.company
            })
          }
        })
      }
    }
  }

  public canReject(): boolean {
    return (this.metaData?.status?.name?.toLowerCase().includes('complete') === false) && (this.metaData?.status?.name?.toLowerCase().includes('rejected') === false)
  }

  public convertLinebreaks(text: string, replace: boolean = true): string {
    if (!text) {
      return ''
    }

    return replace ? text.replace(new RegExp('\n', 'g'), '<br />') : text
  }

  public editContact() {
    this.showModal = 'contact'
  }

  public saveContact() {
    this.showModal = ''

    this.ticketService.updateTicket(this.ticketId, this.ticketContactForm.value).subscribe(
      (data) => {
        this.updateTicket(data)
      },
      (error) => { }
    )
  }

  public editCustomerContact() {
    this.showModal = 'customer_contact'
  }

  public saveCustomerContact() {
    this.showModal = ''

    const formValue = { ...this.customerContactForm.value }
    // Wenn kein Kundenkontakt-Name gesetzt, alle Felder leeren
    if (!formValue.customer_contact_name?.trim()) {
      formValue.customer_contact_salutation = ''
      formValue.customer_contact_name = ''
      formValue.customer_contact_email = ''
    }

    this.ticketService.updateTicket(this.ticketId, formValue).subscribe(
      (data) => {
        this.updateTicket(data)
      },
      (error) => { }
    )
  }

  public removeCustomerContact() {
    this.showModal = ''

    this.ticketService.updateTicket(this.ticketId, {
      customer_contact_salutation: '',
      customer_contact_name: '',
      customer_contact_email: '',
    }).subscribe(
      (data) => {
        this.updateTicket(data)
      },
      (error) => { }
    )
  }

  public setMeta() {
    this.prioForm.controls.priority_id.setValue(this.metaData.priority.id)
    this.statusForm.controls.status_id.setValue(this.metaData.status.id)
  }

  public saveStatus() {
    this.showModal = ''

    if (this.ticket.is_mt) {
      const status_array = this.metaData.status_list.filter((s) => s.id.toString() === this.statusForm.value.status_id.toString())
      const statusObject = status_array[0]

      // if status name contains rejected, dont update status and show alert to user
      if (statusObject.name.toLowerCase().includes('rejected')) {
        window.alert('Ticket kann nicht auf abgelehnt gesetzt werden. Bitte MT Tab in der Mitte nutzen!')
        return
      }
    }

    this.subscriptions.push(
      this.ticketService.updateTicketStatus(this.ticketId, this.statusForm.value).subscribe(
        (data) => {
          this.updateTicket(data)
        },
        (error) => { }
      )
    )
  }

  // Setzt das Ticket über die Schnellaktionen zurück und lädt die Seite neu, um den neuen Stand anzuzeigen (hawk#549)
  public resetTicketAction() {
    this.showModal = ''

    this.subscriptions.push(
      this.ticketService.resetTicket(this.ticketId).subscribe(
        () => {
          window.location.reload()
        },
        (error) => {
          console.error('Ticket konnte nicht zurückgesetzt werden', error)
        }
      )
    )
  }

  public savePrio() {
    this.showModal = ''

    this.subscriptions.push(
      this.ticketService.updateTicket(this.ticketId, this.prioForm.value).subscribe(
        (data) => {
          this.updateTicket(data)
        },
        (error) => { }
      )
    )
  }

  public saveTimeEstimation() {
    this.showModal = ''

    this.subscriptions.push(
      this.ticketService.updateTicket(this.ticketId, this.metaTimeForm.value).subscribe(
        (data) => {
          this.updateTicket(data)
        },
        (error) => { }
      )
    )
  }

  public saveAppointment() {
    this.showModal = ''

    const appointmentData = {
      appointment_contact: this.appointmentForm.value.appointment_contact,
      appointment_date: this.formatDate(this.appointmentForm.value.appointment_date)
    }

    this.subscriptions.push(
      this.ticketService.updateTicket(this.ticketId, appointmentData).subscribe(
        (data) => {
          this.updateTicket(data)
        },
        (error) => { }
      )
    )
  }

  // External number
  public saveExtNr(event: EditEventType) {
    this.metaUpdate('external_order_nr', event)
    this.editExtNr = false
  }

  // External number
  public saveDateSla(event: EditEventType) {
    this.metaUpdate('datesla', event)
    this.editDateSla = false
  }

  // update project
  public saveProject(event: EditEventType) {
    this.metaUpdate('project_id', event)
    this.editProject = false
  }

  public saveStore(storeId: number) {
    this.metaUpdate('store_id', storeId)
    this.editStore = false
  }

  public removeStore() {
    this.metaUpdate('store_id', null)
    this.editStore = false
  }

  public saveDescription(event: EditEventType) {
    this.metaUpdate('description', event)
    this.editDescription = false
  }

  public saveShortInfo(event: EditEventType) {
    this.metaUpdate('short_information', event)
    this.editDescription = false
  }

  public saveKnownSerialNumbers(event: EditEventType) {
    this.metaUpdate('known_serial_numbers', event)
    this.editDescription = false
  }

  public saveDispatcher(dispatcherId: EditEventType) {
    this.metaUpdate('dispatcher_id', dispatcherId)
  }

  public deleteTicket() {
    if (this.ticketDeletable()) {
      const ticketForDialog: TicketForDelete = {
        id: this.ticket.id,
        order_nr: this.ticket.order_nr,
        project_name: this.ticket.project_name,
        status_name: this.metaData?.status?.name
      };

      const dialogRef = this.modal.open(TicketDeleteDialogComponent, {
        inputs: { tickets: [ticketForDialog] }
      });

      dialogRef.closed.subscribe(result => {
        if (result === true) {
          this.subscriptions.push(
            this.ticketService.dangerousDelete(this.ticket.id).subscribe(
              (data) => {
                this.router.navigate([''])
                console.log(data)
              },
              (error) => { console.error(error) }
            )
          )
        }
      });
    } else {
      window.alert('Ticket kann nicht gelöscht werden.')
    }
    this.showModal = ''
  }

  public deleteOperation(operation: Operation) {
    if (this.operationDeletable(operation)) {
      const doIt = window.confirm('Ja, Einsatz wirklich löschen.')
      if (doIt) {
        this.subscriptions.push(
          this.ticketService.deleteOperation(operation.id).subscribe(
            (data) => {
              console.log(data)
              this.loadTicket()
            },
            (error) => { console.error(error) }
          )
        )
      }
    } else {
      window.alert('Einsatz kann nicht gelöscht werden.')
    }

    this.showModal = ''
  }

  public dangerousDelete() {
    const doIt = window.confirm('Ja, Ticket und alle Einsätze wirklich löschen.')
    if (doIt) {
      const challange = window.prompt('Sicher? Zur Bestätigung bitte Ticketnummer eingeben.')
      if (challange === this.ticketId) {
        this.subscriptions.push(
          this.ticketService.dangerousDelete(parseInt(this.ticketId)).subscribe(
            {
              next: (data) => {
                console.log(data)
                this.router.navigate([''])
              },
              error: (error) => {
                console.error(error)
                window.alert(error)
                this.showModal = ''
              }
            }
          )
        )
      } else {
        window.alert('Ticket ID falsch. Löschen nicht gestartet.')
        this.showModal = ''
      }
    } else {
      window.alert('Einsatz kann nicht gelöscht werden.')
      this.showModal = ''
    }
  }

  public dangerousDeleteOperation(opertionId: number) {
    const doIt = window.confirm(`Ja, Einsatz (${opertionId}) wirklich löschen.`)
    if (doIt) {
      const challange = window.prompt('Sicher? Zur Bestätigung bitte Ticketnummer eingeben.')
      if (challange === this.ticketId) {
        this.subscriptions.push(
          this.operationService.dangerousDelete(opertionId).subscribe(
            {
              next: (data) => {
                console.log(data)
                window.location.reload();
              },
              error: (error) => {
                console.error(error)
                window.alert(error)
                this.showModal = ''
              }
            }
          )
        )
      } else {
        window.alert('Ticket ID falsch. Löschen nicht gestartet.')
        this.showModal = ''
      }
    } else {
      window.alert('Einsatz kann nicht gelöscht werden.')
      this.showModal = ''
    }
  }

  public ticketDeletable(): boolean {
    return this.ticket.deletable
  }

  public operationDeletable(operation: Operation): boolean {
    return operation.deletable
  }

  public metaUpdate(key, value): Promise<void> {
    const changeData = {}
    changeData[key] = value

    this.showModal = ''

    return new Promise(resolve => {
      this.subscriptions.push(
        this.ticketService.updateTicket(this.ticketId, changeData).subscribe(
          (data) => {
            this.updateTicket(data)
            resolve()
          },
          (error) => {
            resolve()
          }
        )
      )
    })
  }

  public technicianPresent(): boolean {
    return this.selectedOperation && this.selectedOperation.technicians.length > 0
  }

  public mainTechnician(): OperationTechnicianEntry {
    if (this.technicianPresent()) {
      return this.selectedOperation.technicians[0]
    }

    return
  }

  public operationDate(): Date {
    if (this.technicianPresent()) {
      return new Date(this.selectedOperation.operation_date)
    }

    return
  }

  public technicianWarehousePresent(): boolean {
    return this.technicianPresent() && this.mainTechnician().warehouses.length > 0
  }

  public mainTechnicianWarehouse(): number {
    if (this.technicianWarehousePresent()) {
      return this.mainTechnician().warehouses[0]
    }

    return
  }

  public closeModal() {
    this.setMeta()
    this.showModal = ''
  }

  // Anzahl der konfigurierten Stationen im store_info (für Anzeige in Aktionen-Card).
  public get storeInfoStationCount(): number {
    return Object.keys(this.ticket?.store_info?.stations ?? {}).length
  }

  // Öffnet den Filialinfo-Editor (StoreInfoEditorComponent) als Modal-Dialog.
  // Nach erfolgreichem Speichern liefert das Modal das aktualisierte Ticket zurück.
  public openStoreInfoEditor(): void {
    if (!this.ticket) return
    const dialogRef = StoreInfoEditorComponent.open(this.modal, { ticket: this.ticket })
    dialogRef.closed.subscribe((updated: Ticket | undefined) => {
      if (updated) {
        this.ticket = updated
      }
    })
  }

  public selectStep(step: OperationMetadata) {
    this.selectedStep = step
    this.resetStoreUnit.next()
    this.viewportScroller.scrollToAnchor('storeSubbox')
  }

  public disableMtStatus(status: Status): boolean {
    if (this.ticket.is_mt && status.name.toLowerCase().includes('abgelehnt')) {
      return true
    } else {
      return false
    }
  }

  private formatDate(date) {
    if (date) {
      return date.getFullYear() + '-' +
        ((date.getMonth() + 1).toString().padStart(2, '0')) + '-' +
        date.getDate().toString().padStart(2, '0') + 'T' +
        date.getHours().toString().padStart(2, '0') + ':' +
        date.getMinutes().toString().padStart(2, '0') + ':00'
    }

    return
  }

  private loadTicketGroup() {
    this.subscriptions.push(
      this.ticketService.getTicketGroup(this.ticket.id).subscribe(
        data => {
          this.ticketGroup = data.ticket_group
        },
        error => {
          console.error(error)
        }
      )
    )
  }

  onTicketGroupHawkIdsInputChanged() {
    this.ticketGroupHawkIdsInputChanged$.next();
  }

  validateTicketGroupHawkIds() {
    const res: { ticket: Ticket, add_to_group: boolean, info: string, unite_with_group?: TicketGroup }[] = []

    if (this.addHawkIdsToTicketGroup) {
      const order_nrs = [...new Set(this.addHawkIdsToTicketGroup.replaceAll(' ', '').split(';').filter(item => item))]

      const promises = Promise.all(
        order_nrs.map(order_nr => {
          return new Promise<{ ticket: Ticket, ticket_group?: TicketGroup }>(resolve => {
            this.ticketService.findTicketGroupByOrderNr(order_nr).subscribe(
              data => {
                resolve(data)
              },
              error => {
                console.error(error)
                resolve(null)
              }
            )
          })
        })
      )

      promises.then(
        data => {
          for (const item of data) {
            if (item?.ticket) {
              if (item.ticket.id == this.ticket.id) {
                const ticket_is_not_in_group = !this.ticketGroup
                res.push({
                  ticket: item.ticket,
                  add_to_group: ticket_is_not_in_group,
                  info: ticket_is_not_in_group ? 'Ohne Gruppe' : 'Bereits in der selben Gruppe'
                })
              } else {
                if (item.ticket_group) {
                  if (item.ticket_group.id == this.ticketGroup?.id) {
                    res.push({
                      ticket: item.ticket,
                      add_to_group: false,
                      info: 'Bereits in der selben Gruppe'
                    })
                  } else {
                    res.push({
                      ticket: item.ticket,
                      add_to_group: true,
                      info: `Bereits in Gruppe mit ${item.ticket_group.tickets.length} Ticket(s): Gruppe wird zusammengeführt`,
                      unite_with_group: item.ticket_group
                    })
                  }
                } else {
                  res.push({
                    ticket: item.ticket,
                    add_to_group: true,
                    info: 'Ohne Gruppe'
                  })
                }
              }
            }
          }
        }
      )
    }

    this.addTicketsToTicketGroup = res
  }

  async saveTicketsToTicketGroup() {

    this.saveTicketGroupProcess = true

    if (!this.ticketGroup) {
      await this.createTicketGroup()
    }

    if (this.ticketGroup) {
      // Step 1: unite Groups
      const unite_with_group_ids = [...new Set(this.addTicketsToTicketGroup.filter(item => item.add_to_group && item.unite_with_group && (item.unite_with_group.id != this.ticketGroup.id)).map(item => item.unite_with_group.id))]
      for (const group_id of unite_with_group_ids) {
        await new Promise<void>(resolve => {
          this.ticketGroupService.uniteGroups(this.ticketGroup.id, group_id).subscribe(
            data => {
              this.ticketGroup = data
              resolve()
            },
            error => {
              console.error(error)
              resolve()
            }
          )
        })
      }

      // Step 2: add tickets without Group
      const add_ticket_ids = [...new Set(this.addTicketsToTicketGroup.filter(item => item.add_to_group && !item.unite_with_group && item.ticket.id != this.ticket.id).map(item => item.ticket.id))]
      for (const id of add_ticket_ids) {
        await new Promise<void>(resolve => {
          this.ticketService.assignTicketToGroup(id, this.ticketGroup.id).subscribe(
            data => {
              this.ticketGroup = data
              resolve()
            },
            error => {
              console.error(error)
              resolve()
            }
          )
        })
      }
    }

    this.saveTicketGroupProcess = false
    this.addHawkIdsToTicketGroup = null
    this.addTicketsToTicketGroup = []
    if (this.showModal == 'ticket_group') this.showModal = ''
  }

  createTicketGroup() {
    return new Promise<void>(resolve => {
      this.ticketService.createTicketGroup(this.ticket.id, { name: 'Gruppe - ' + this.ticket.order_nr }).subscribe(
        data => {
          this.ticketGroup = data
          resolve()
        },
        error => {
          console.error(error)
          resolve()
        }
      )
    })
  }

  openRemoveTicketFromGroupDialog(id: number) {
    if (confirm('Ja, Ticket wirklich aus Gruppe entfernen.')) {
      this.ticketService.removeTicketFromGroup(id).subscribe(
        data => {
          this.ticketGroup = data
        },
        error => {
          console.error(error)
        }
      )
    }
  }

  shortName(name: string) {
    return name.split(' ').map(part => part ? part[0] : '').join('')
  }

  // normalize address format: address_street_no is deprecated and should be included in address_street
  normalizeAddressStreetFormat(ticket: Ticket): void {
    if (!ticket.store_id && ticket.address_street_no) {
      ticket.address_street += ' ' + ticket.address_street_no
    }
  }

  updateNormalizedAddressStreet($event: EditEventType) {
    if (this.ticket.address_street_no) {
      // first delete address_street_no
      this.metaUpdate('address_street_no', null).then(_ => {
        // then update completed address_street
        this.metaUpdate('address_street', $event)
      })
    } else {
      this.metaUpdate('address_street', $event)
    }
  }
}
